<template>
  <div class="case-fluid-list gray-3 bg">
    <switch-tree
      :items="fluidItems"
      :selected="selectedFluid ? selectedFluid.key : null"
      :readonly="readonly"
      @toggle="toggleItem"
      @select="selectItem"
    >
      <template slot="first-item-slot">
        <div class="new-fluid-row" data-testid="new-fluid-container">
          <md-button
            class="md-primary md-outlined-button"
            :disabled="readonly"
            aria-label="Add New Fluid"
            data-testid="add-new-fluid-button"
            @click="showNewFluidDialog = true"
          >
            <md-icon>add</md-icon>
            New Fluid
          </md-button>
          <help-tooltip v-if="fluidCountError" type="error" aria-label="Too Many Fluids Error" data-testid="too-many-fluids-error">
            {{ fluidCountError.message }}
          </help-tooltip>
        </div>
      </template>
    </switch-tree>
    <div class="global-toggle-container gray-3 bg" data-testid="toggle-all-fluids-container">
      <toggle-switch
        class="global-toggle md-primary ui-theme-3"
        :value="allFluidsToggled"
        :readonly="readonly"
        aria-label="Toggle All Fluids"
        data-testid="toggle-all-fluids-switch"
        @toggle="value => toggleAllFluids(value)"
      />
      <span class="global-toggle-label">Off/On All Fluids</span>
    </div>
    <new-fluid-dialog
      :show="showNewFluidDialog"
      data-testid="new-fluid-dialog-container"
      @cancel="cancelNewFluid"
      @submit="submitNewFluid"
    />
  </div>
</template>

<script>
import sortBy from 'lodash/sortBy';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import Chance from 'chance';

import { fluids } from '~lib/common/config/fluids';
import { infusionBagVolumes } from '~lib/common/config/medication';

import HelpTooltip from '~lib/common/components/HelpTooltip';
import SwitchTree from '../../common/SwitchList/SwitchTree';
import ToggleSwitch from '../../common/ToggleSwitch';
import NewFluidDialog from './NewFluidDialog';

const chance = new Chance();

export default {
  name: 'case-fluid-list',
  components: {
    HelpTooltip,
    SwitchTree,
    ToggleSwitch,
    NewFluidDialog,
  },
  props: {
    caseFluids: {
      type: Array,
      default: () => []
    },
    customFluids: {
      type: Array,
      default: () => []
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    selectedFluid: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      showNewFluidDialog: false,
      state: {
        sortedFluids: [],
      }
    };
  },
  computed: {
    fluidItems() {
      return this.state.sortedFluids.map(fluid => {
        const caseFluid = this.caseFluids.find(cf => cf.key === fluid.key);
        let fluidErrors;
        if (caseFluid) {
          fluidErrors = get(this.$store.getters['validation/errorsByFieldAndId'], `fluids.${caseFluid.id}`);
        } else {
          fluidErrors = get(this.$store.getters['validation/errorsByFieldAndId'], `fluids.${fluid.key}`);
        }
        return {
          key: fluid.key,
          label: fluid.abbreviation.en,
          on: !!caseFluid,
          error: !!fluidErrors,
          tooltip: fluidErrors && fluidErrors.length ? fluidErrors[0].message : null,
        };
      });
    },
    allFluidsToggled() {
      return !!(
        !!this.caseFluids &&
        this.caseFluids.length &&
        this.state.sortedFluids.length <= this.caseFluids.length
      );
    },
    fluidCountError() {
      let errors = []
      if (this.$store) {
        errors = get(this.$store.getters['validation/errorsByFieldAndId'], 'fluids.fluid-count', []);
      }
      return errors.length > 0 ? errors[0] : null;
    },
  },
  watch: {
    customFluids() {
      this.initializeFluids();
    },
  },
  created() {
    this.initializeFluids(); // sort only on load so enable/disable doesn't cause order to change until refresh
  },
  methods: {
    toggleItem(item, params) {
      const toggleOn = params.value;
      let updatedCaseFluids = this.caseFluids || [];
      if (toggleOn) {
        if (!updatedCaseFluids.find(f => f.key === item.key)) {
          const fluid = this.state.sortedFluids.find(f => f.key === item.key);
          updatedCaseFluids.push(this.createCaseFluid(fluid));
          this.$analytics.event('Authoring Tool', 'fluids_name_selected', fluid.label.en);
        }
      } else {
        updatedCaseFluids = updatedCaseFluids.filter(cf => cf.key !== item.key);
      }
      this.$emit('updateCase', { fluids: updatedCaseFluids });
    },
    toggleAllFluids(toggleOn) {
      if (toggleOn) {
        const updatedCaseFluids = this.state.sortedFluids.map(fluid => {
          const existingCaseFluid = (this.caseFluids || []).find(cf => cf.key === fluid.key);
          if (existingCaseFluid) {
            return existingCaseFluid;
          } else {
            return this.createCaseFluid(fluid);
          }
        });
        this.$emit('updateCase', { fluids: updatedCaseFluids });
      } else {
        this.$emit('updateCase', { fluids: [] });
      }
    },
    createCaseFluid(fluid) {
      return {
        id: chance.guid(),
        key: fluid.key,
        infusionDosingTypes: cloneDeep(fluid.defaultDosingTypes),
        infusionBagVolumes: cloneDeep(fluid.defaultBagVolumes)
      };
    },
    selectItem(item) {
      let fluid = fluids.find(f => f.key === item.key);
      if (!fluid) {
        fluid = (this.customFluids || []).find(cf => cf.key === item.key);
      }
      this.$emit('select', fluid);
    },
    initializeFluids() {
      const productType = this.$featureAvailabilityService.productType();
      const isEnabled = (f) => !!(this.caseFluids || []).find(cf => cf.key === f.key);
      const isAvailable = (f) => f.productTypes.includes(productType) || isEnabled(f);
      const allFluids = fluids
        .filter(f => isAvailable(f))
        .concat(this.customFluids || []);
      this.state.sortedFluids = sortBy(allFluids, f => isEnabled(f) ? 0 : 1);
    },
    submitNewFluid(fluidData) {
      this.showNewFluidDialog = false;
      const customFluidKey = chance.guid();
      const newCustomFluid = {
        key: customFluidKey,
        label: {
          en: fluidData.name
        },
        abbreviation: {
          en: fluidData.name
        },
        availableBagVolumes: infusionBagVolumes.map(volume => volume.key),
      };
      const newFluid = {
        id: chance.guid(),
        key: customFluidKey,
        infusionBagVolumes: fluidData.bagVolumes,
        infusionDosingTypes: [ 'ML_HR' ]
      };

      const caseUpdates = {};
      caseUpdates.customFluids = this.customFluids || [];
      caseUpdates.customFluids.push(newCustomFluid);
      caseUpdates.fluids = this.caseFluids || [];
      caseUpdates.fluids.push(newFluid);
      this.$emit('updateCase', caseUpdates);
    },
    cancelNewFluid() {
      this.showNewFluidDialog = false;
    },
  },
}
</script>

<style scoped>
.new-fluid-row {
  display: flex;
  align-items: center;
}

.global-toggle-container {
  padding-top: 1rem;
  display: flex;
  align-items: center;
}

.global-toggle-label {
  margin-left: 0.4rem;
  color: #0C70B0;
}
</style>